import { useMemo } from 'react';
import { ListItemButton } from '@mui/material';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { queueModal } from '@components/Modals/modalSlice';
import { AppDispatch, useAppDispatch } from '@reduxStore';
import { queueUncontrolledToast } from '@toast';
import { toSendPudlReceiptProps } from '@/PickupDeliveryReceipt/types';

/** Each receipt is generated sequentially in one backend request, so cap the batch to keep it from tying up a PASOE agent long enough to time out other requests. */
const MAX_RECEIPTS = 50;

/** The receipts are emailed or texted to a single driver, so orders assigned to different drivers can only be printed */
function hasMultipleDrivers(legs: DispatchLeg[]) {
	return new Set(legs.map((leg) => leg.drvId ?? null)).size > 1;
}

/**
 * Opens the Pickup/Delivery Receipt modal for one or more dispatch board legs.  Shared by the action menu and the Driver cell's send icon
 * so a multi-row selection behaves the same from either.
 */
export function openPickupDeliveryReceipts(appDispatch: AppDispatch, legs: DispatchLeg[]) {
	if (legs.length > MAX_RECEIPTS) {
		appDispatch(
			queueUncontrolledToast({
				type: 'message',
				message: `You have ${String(legs.length)} orders selected. Please select no more than ${String(MAX_RECEIPTS)} orders at a time for Pickup/Delivery Receipts.`,
				severity: 'warning'
			})
		);
		return;
	}

	appDispatch(
		queueModal({
			Component: 'sendPudlReceiptModal',
			Props: { ...toSendPudlReceiptProps(legs[0]), legs, printOnly: hasMultipleDrivers(legs) }
		})
	);
}

/**
 * Displays, emails, or texts the Pickup/Delivery Receipts for one or more selected dispatch board legs.
 */
export default function PrintPickupDeliveryReceipts() {
	const appDispatch = useAppDispatch();
	const gridApiRef = useGridApiContext();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	const legs = useMemo(() => Array.from(selectedRowIds.values()) as DispatchLeg[], [selectedRowIds]);
	const isPrintOnly = useMemo(() => hasMultipleDrivers(legs), [legs]);

	// This action requires 1 or more rows to be selected
	if (legs.length === 0) {
		return <></>;
	}

	return (
		<ListItemButton
			onClick={() => {
				openPickupDeliveryReceipts(appDispatch, legs);
			}}>
			{isPrintOnly ? 'Print' : 'Print/Send'} Pickup/Delivery Receipt{legs.length > 1 ? 's' : ''}
		</ListItemButton>
	);
}
